import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  RotateCcw, 
  Star, 
  Clock, 
  Shield, 
  Trophy, 
  Flame, 
  BookOpen, 
  ChevronRight, 
  ChevronLeft, 
  Zap, 
  Users, 
  TrendingUp,
  CheckCircle,
  Play,
  Award,
  Heart,
  Brain,
  Moon,
  Sun,
  Wind,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useAuth } from '../../hooks/AuthContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Module, UserProgress, MeditationCategory } from '../../types';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';

export const Meditations: React.FC = () => {
  const { user } = useAuth();
  const { addEvent } = useAnalytics();
  const [modules, setModules] = useState<Module[]>([]);
  const [meditationCategories, setMeditationCategories] = useState<MeditationCategory[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'order' | 'newest' | 'popular' | 'duration'>('order');
  const [selectedDuration, setSelectedDuration] = useState<string>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Duration options for meditation
  const durationOptions = [
    { value: 'all', label: 'All Durations' },
    { value: 'short', label: 'Short (1-5 min)', min: 1, max: 5 },
    { value: 'medium', label: 'Medium (6-15 min)', min: 6, max: 15 },
    { value: 'long', label: 'Long (16+ min)', min: 16, max: 999 }
  ];

  const getTimestampValue = (value: any): number => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'object' && typeof value.toDate === 'function') {
      return value.toDate().getTime();
    }
    return 0;
  };

  // Calculate meditation stats
  const stats = useMemo(() => {
    const completedModules = userProgress?.completedModules || [];
    const completedMeditations = modules.filter(module => completedModules.includes(module.id));
    const completedMinutes = completedMeditations.reduce((total, module) => total + (module.estMinutes || 0), 0);
    
    return {
      totalMeditations: modules.length,
      completedMeditations: completedMeditations.length,
      completedMinutes,
      streak: userProgress?.streak || 0
    };
  }, [modules, userProgress]);

  // Filter and sort modules
  const filteredModules = useMemo(() => {
    let filtered = modules.filter(module => {
      const matchesSearch = module.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           module.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Only show meditation modules
      const isMeditationModule = module.moduleType === 'meditation';
      
      // Filter by duration
      let matchesDuration = true;
      if (selectedDuration !== 'all') {
        const duration = durationOptions.find(d => d.value === selectedDuration);
        if (duration && module.estMinutes) {
          matchesDuration = module.estMinutes >= duration.min && module.estMinutes <= duration.max;
        }
      }
      
      return matchesSearch && isMeditationModule && matchesDuration;
    });

    // Sort modules
    switch (sortBy) {
      case 'order':
        // Sort by order field (ascending) - Day 1, Day 2, Day 3, etc.
        filtered.sort((a, b) => {
          const orderA = typeof a.order === 'number' ? a.order : 999;
          const orderB = typeof b.order === 'number' ? b.order : 999;
          return orderA - orderB;
        });
        break;
      case 'popular':
        filtered.sort((a, b) => (b.completionCount || 0) - (a.completionCount || 0));
        break;
      case 'duration':
        filtered.sort((a, b) => (a.estMinutes || 0) - (b.estMinutes || 0));
        break;
      case 'newest':
        filtered.sort((a, b) => getTimestampValue(b.createdAt) - getTimestampValue(a.createdAt));
        break;
      default:
        // Default to order
        filtered.sort((a, b) => {
          const orderA = typeof a.order === 'number' ? a.order : 999;
          const orderB = typeof b.order === 'number' ? b.order : 999;
          return orderA - orderB;
        });
        break;
    }

    return filtered;
  }, [modules, searchTerm, selectedDuration, sortBy]);

  // Load meditation modules and user progress
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Loading meditation modules for users...');
        
        // Load meditation categories first
        const categoriesQuery = query(
          collection(db, 'meditationCategories'),
          where('isActive', '==', true),
          orderBy('order', 'asc')
        );
        const categoriesSnapshot = await getDocs(categoriesQuery);
        const categoriesData = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        })) as MeditationCategory[];
        setMeditationCategories(categoriesData);
        console.log('📊 Found', categoriesData.length, 'meditation categories');
        
        // Load modules - try different published field variations
        let modulesSnapshot;
        try {
          // First try with 'published' field and order by 'order' field
          const modulesQuery = query(
            collection(db, 'modules'),
            where('published', '==', true),
            orderBy('order', 'asc')
          );
          modulesSnapshot = await getDocs(modulesQuery);
          console.log('📊 Found', modulesSnapshot.docs.length, 'modules with published=true');
        } catch (publishedError) {
          console.warn('⚠️ Published query with order failed, trying without order:', publishedError);
          try {
            // Try without order field
            const modulesQuery = query(
              collection(db, 'modules'),
              where('published', '==', true)
            );
            modulesSnapshot = await getDocs(modulesQuery);
            console.log('📊 Found', modulesSnapshot.docs.length, 'modules with published=true (no order)');
          } catch (noOrderError) {
            console.warn('⚠️ Published query failed, trying isPublished:', noOrderError);
            try {
              // Try with 'isPublished' field
              const modulesQuery = query(
                collection(db, 'modules'),
                where('isPublished', '==', true),
                orderBy('order', 'asc')
              );
              modulesSnapshot = await getDocs(modulesQuery);
              console.log('📊 Found', modulesSnapshot.docs.length, 'modules with isPublished=true');
            } catch (isPublishedError) {
              console.warn('⚠️ isPublished query failed, trying simple query:', isPublishedError);
              // Fallback to simple query and filter manually
              const modulesQuery = query(
                collection(db, 'modules')
              );
              modulesSnapshot = await getDocs(modulesQuery);
              console.log('📊 Found', modulesSnapshot.docs.length, 'total modules');
            }
          }
        }
        
        const allModules = modulesSnapshot.docs.map(doc => {
           const data = doc.data();
           console.log('📝 Module:', doc.id, data.title, 'published:', data.published, 'isPublished:', data.isPublished, 'order:', data.order);
           return {
             id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() ?? data.createdAt ?? null,
            updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt ?? null,
            order: data.order ?? 999 // Default high order for items without order field
           };
         }) as Module[];
        
        // Filter for published modules manually if needed
        const publishedModules = allModules.filter(module => {
          const isPublished = module.published === true || module.isPublished === true;
          console.log('🔍 Module', module.title, 'published status:', isPublished);
          return isPublished;
        });
        
        // Filter for meditation modules
        const meditationModules = publishedModules.filter(module => 
          module.moduleType === 'meditation'
        );
        
        // Sort by order field (ascending) - items without order will be at the end
        meditationModules.sort((a, b) => {
          const orderA = typeof a.order === 'number' ? a.order : 999;
          const orderB = typeof b.order === 'number' ? b.order : 999;
          return orderA - orderB;
        });
        
        console.log('✅ Loaded', meditationModules.length, 'published meditation modules for users (sorted by order)');
        setModules(meditationModules);

        // Load user progress
        if (user) {
          const progressQuery = query(
            collection(db, 'userProgress'),
            where('uid', '==', user.uid)
          );
          const progressSnapshot = await getDocs(progressQuery);
          if (!progressSnapshot.empty) {
            const progressData = progressSnapshot.docs[0].data() as UserProgress;
            setUserProgress(progressData);
          }
        }
      } catch (error) {
        console.error('Error loading meditation data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading meditations...</p>
        </div>
      </div>
    );
  }

  // Helper function to get modules by category
  const getModulesByCategory = (categoryId: string) => {
    return filteredModules.filter(module => module.meditationCategory === categoryId);
  };

  // Helper function to toggle category expansion
  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Helper function to get category color classes
  const getCategoryColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      purple: 'from-purple-500 to-purple-700',
      blue: 'from-blue-500 to-blue-700',
      green: 'from-green-500 to-green-700',
      pink: 'from-pink-500 to-pink-700',
      indigo: 'from-indigo-500 to-indigo-700'
    };
    return colorMap[color] || 'from-purple-500 to-purple-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      <div className="px-4 md:px-4 py-4 md:py-8 max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-lg md:rounded-2xl p-4 md:p-8 text-white shadow-xl mb-6 md:mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-white bg-opacity-20 p-3 rounded-xl">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold">Meditations</h1>
              <p className="text-purple-100 text-sm md:text-lg">Find peace and mindfulness</p>
            </div>
          </div>
          
          {/* Meditation Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{stats.totalMeditations}</div>
              <div className="text-sm text-purple-100">Total Meditations</div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{stats.completedMeditations}</div>
              <div className="text-sm text-purple-100">Completed</div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{stats.completedMinutes}</div>
              <div className="text-sm text-purple-100">Minutes</div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{stats.streak}</div>
              <div className="text-sm text-purple-100">Day Streak</div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mb-6 md:mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search meditations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Meditation Categories */}
        <div className="space-y-6">
          {meditationCategories.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No meditation categories found</h3>
                <p className="text-gray-600">
                  Meditation categories need to be created by an administrator.
                </p>
              </div>
            </div>
          ) : (
            meditationCategories.map((category) => {
              const categoryModules = getModulesByCategory(category.id);
              const isExpanded = expandedCategories.has(category.id);
              
              return (
                <div key={category.id} className="bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
                  {/* Category Header */}
                  <div 
                    className={`bg-gradient-to-r ${getCategoryColorClasses(category.color)} p-6 cursor-pointer`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="bg-white bg-opacity-20 p-3 rounded-xl">
                          <Heart className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-white">{category.name}</h2>
                          {category.description && (
                            <p className="text-white text-opacity-90 text-sm mt-1">{category.description}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-white text-opacity-80 text-sm">
                            <span>{categoryModules.length} meditations</span>
                            <span>{categoryModules.reduce((total, module) => total + (module.estMinutes || 0), 0)} minutes total</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-white">
                        {isExpanded ? (
                          <ChevronUp className="w-6 h-6" />
                        ) : (
                          <ChevronDown className="w-6 h-6" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Category Content - Accordion */}
                  {isExpanded && (
                    <div className="p-6">
                      {categoryModules.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Heart className="w-6 h-6 text-gray-400" />
                          </div>
                          <p className="text-gray-500">No meditations in this category yet</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {categoryModules.map((module) => {
                            const isCompleted = userProgress?.completedModules?.includes(module.id) || false;
                            const completionPercentage = userProgress?.moduleProgress?.[module.id] || 0;
                            
                            return (
                              <Link
                                key={module.id}
                                to={`/meditations/${module.id}`}
                                className="group block"
                                onClick={() => addEvent('meditation_started', { moduleId: module.id })}
                              >
                                <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border border-gray-200">
                                  <div className="flex items-start justify-between mb-2">
                                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                                      {module.title}
                                    </h3>
                                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                                      <Clock className="w-4 h-4" />
                                      <span>{module.estMinutes}m</span>
                                    </div>
                                  </div>
                                  
                                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                    {module.summary}
                                  </p>
                                  
                                  {/* Module Tags */}
                                  {module.tags && module.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-3">
                                      {module.tags.slice(0, 2).map((tag, index) => (
                                        <span
                                          key={index}
                                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                                        >
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Action Button */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                                      {isCompleted ? (
                                        <>
                                          <CheckCircle className="w-4 h-4 text-green-500" />
                                          <span className="text-green-600 font-medium">Completed</span>
                                        </>
                                      ) : (
                                        <>
                                          <Play className="w-4 h-4 text-purple-500" />
                                          <span className="text-purple-600 font-medium">Start</span>
                                        </>
                                      )}
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-purple-500 transition-colors" />
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Clear Search Button */}
        {searchTerm && (
          <div className="text-center mt-8">
            <button
              onClick={() => setSearchTerm('')}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Clear Search
            </button>
          </div>
        )}
      </div>
    </div>
  );
};